'use client';
import React from 'react';
import { Cat, CatKey, loadPrefs, savePrefs, needConsent, setConsented, initialCats } from '../utils/cookie-prefs';

export default function CookiesPage() {
  const [cats, setCats] = React.useState<Cat[]>(initialCats);
  const [showBanner, setShowBanner] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState('');

  React.useEffect(() => {
    setCats(loadPrefs());
    setShowBanner(needConsent());
  }, []);

  const setVal = (k: CatKey, v: boolean) => setCats(prev =>
    prev.map(c => c.key === k && !c.lock ? { ...c, val: v } : c)
  );

  const save = (mode?: 'all' | 'rej') => {
    let newCats = cats;

    if (mode === 'all') {
      newCats = cats.map(c => ({ ...c, val: true }));
    } else if (mode === 'rej') {
      newCats = cats.map(c => c.lock ? c : { ...c, val: false });
    }

    setCats(newCats);
    savePrefs(newCats);
    setConsented();
    setShowBanner(false);
    setStatusMessage('Предпочтения сохранены. Настройки вступят в силу при следующей загрузке страницы.');

    if (typeof window !== 'undefined') {
      setTimeout(() => window.location.reload(), 100);
    }
  };

  const acceptAll = () => save('all');
  const rejectAll = () => save('rej');
  const saveSelected = () => save();

  const exportJson = () => {
    if (typeof window === 'undefined') return;
    const data = localStorage.getItem('interim_cookie_prefs_v1') || '{}';
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'cookie_prefs.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-extrabold mb-3">Файлы cookies</h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl">
            Мы уважаем вашу конфиденциальность. Здесь вы можете узнать, какие данные мы собираем, как их используем, и управлять своими предпочтениями.
          </p>
          <div className="mt-6 flex gap-3">
            <a href="#manager" className="px-4 py-3 rounded-xl font-semibold" style={{ background: '#FFC300', color: '#102a43' }}>Открыть настройки</a>
            <button onClick={exportJson} className="px-4 py-3 rounded-xl font-semibold transition-colors duration-200 hover:bg-white/20" style={{ background: 'rgba(255,255,255,.1)' }}>Экспортировать предпочтения</button>
          </div>
        </div>
      </header>

      <main className="px-6 py-10 md:py-14 max-w-5xl mx-auto space-y-10">
        {statusMessage && (
          <div className="p-4 rounded-xl text-sm bg-green-600/50 border border-green-400/50">
            {statusMessage}
          </div>
        )}

        <section className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
          <h2 className="text-2xl font-bold mb-3">Что такое cookies?</h2>
          <p className="text-gray-200">Cookies — это небольшие текстовые файлы, которые сайт сохраняет в вашем браузере. Они помогают запоминать ваши настройки, измерять производительность сайта и показывать контент, который вам действительно полезен.</p>
          <p className="text-sm text-gray-300 mt-2">Последнее обновление: <b>18 ноября 2025</b></p>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <article className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
            <h3 className="text-xl font-semibold">Категории cookies</h3>
            <ul className="mt-3 list-disc ml-6 space-y-2 text-gray-200">
              <li><b>Строго необходимые</b> — обеспечение работы сайта (сеанс, безопасность, корзина и т.п.). Нельзя отключить.</li>
              <li><b>Аналитика</b> — анонимная статистика посещений (улучшение UX, Core Web Vitals).</li>
              <li><b>Функциональные</b> — запоминают ваши выборы (язык, регион, формы).</li>
              <li><b>Маркетинговые</b> — персонализация предложений, ретаргетинг.</li>
            </ul>
          </article>

          <article className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
            <h3 className="text-xl font-semibold">Как изменить настройки в браузере</h3>
            <p className="text-gray-200">Вы можете управлять cookies в настройках браузера:</p>
            <ul className="mt-3 list-disc ml-6 text-gray-200 space-y-1">
              <li>Chrome: <span className="p-1 rounded">Настройки → Конфиденциальность → Файлы cookie</span></li>
              <li>Safari: <span className="p-1 rounded">Settings → Privacy → Cookies</span></li>
              <li>Firefox: <span className="p-1 rounded">Settings → Privacy & Security → Cookies</span></li>
              <li>Edge: <span className="p-1 rounded">Settings → Cookies and site permissions</span></li>
            </ul>
          </article>
        </section>

        <section id="manager" className="rounded-2xl p-6 scroll-mt-32" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
          <h2 className="text-2xl font-bold">Менеджер согласия</h2>
          <p className="text-gray-200 mt-1">Управляйте категориями. Ваш выбор сохраняется в браузере и может быть изменён в любой момент.</p>
          <div className="mt-5 divide-y divide-white/10">
            {cats.map(c => (
              <div key={c.key} className="py-4 flex items-start gap-4">
                <label className="relative w-12 h-7 rounded-full cursor-pointer" style={{ background: c.lock ? '#2d3748' : '#334155' }}>
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={c.val}
                    disabled={!!c.lock}
                    onChange={e => setVal(c.key, e.target.checked)}
                  />
                  <span className={`absolute top-[6px] left-[6px] w-5 h-5 rounded-full transition-all 
                                        ${c.val ? 'bg-sky-500 peer-checked:left-6' : 'bg-white'} 
                                        ${c.lock && 'opacity-50 cursor-not-allowed'}
                                    `} />
                </label>
                <div>
                  <div className="font-semibold">{c.name} {c.lock && <span className="text-xs text-gray-300">(обязательные)</span>}</div>
                  <div className="text-sm text-gray-200">{c.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={acceptAll} className="px-4 py-3 rounded-xl font-semibold transition-opacity duration-200 hover:opacity-90" style={{ background: '#FFC300', color: '#102a43' }}>Принять всё</button>
            <button onClick={rejectAll} className="px-4 py-3 rounded-xl font-semibold transition-colors duration-200 hover:bg-white/20" style={{ background: 'rgba(255,255,255,.1)' }}>Отклонить необязательные</button>
            <button onClick={saveSelected} className="px-4 py-3 rounded-xl font-semibold transition-colors duration-200 hover:bg-white/20" style={{ background: 'rgba(255,255,255,.1)' }}>Сохранить выбор</button>
          </div>

          <p className="text-sm text-gray-300 mt-3">
            Нажимая «Сохранить выбор», вы соглашаетесь с использованием выбранных категорий. Подробности — в
            <a href="/privacy" className="underline decoration-[#FFE07A] decoration-2 ml-1">Политике конфиденциальности</a>.
          </p>
        </section>

        <section className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
          <h3 className="text-xl font-semibold">Примеры cookies, которые может использовать сайт</h3>
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full text-sm">
              <thead className="text-left text-gray-200"><tr><th className="py-2 pr-6">Имя</th><th className="py-2 pr-6">Категория</th><th className="py-2 pr-6">Срок</th><th className="py-2">Назначение</th></tr></thead>
              <tbody className="text-gray-100">
                <tr><td className="py-2 pr-6">interim_session</td><td className="py-2 pr-6">Строго необходимые</td><td className="py-2 pr-6">Сеанс</td><td className="py-2">Поддержка входа и безопасности.</td></tr>
                <tr><td className="py-2 pr-6">_ym_uid / _ym_d</td><td className="py-2 pr-6">Аналитика</td><td className="py-2 pr-6">1 год</td><td className="py-2">Анонимная статистика посещений (Яндекс.Метрика).</td></tr>
                <tr><td className="py-2 pr-6">interim_locale</td><td className="py-2 pr-6">Функциональные</td><td className="py-2 pr-6">6 мес.</td><td className="py-2">Запоминает язык/регион.</td></tr>
                <tr><td className="py-2 pr-6">_fbp / _fbc</td><td className="py-2 pr-6">Маркетинговые</td><td className="py-2 pr-6">3 мес.</td><td className="py-2">Пиксель для ретаргетинга (Meta/Facebook).</td></tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {showBanner && (
        <div className="fixed left-0 right-0 bottom-0 z-50">
          <div className="mx-auto max-w-5xl m-3 rounded-2xl p-5" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
            <div className="md:flex md:items-center md:justify-between gap-4">
              <p className="text-sm md:text-base text-gray-100">Мы используем cookies для работы сайта, аналитики и персонализации. Узнайте больше в <a href="/cookies" className="underline font-semibold">Политике cookies</a>.</p>
              <div className="mt-3 md:mt-0 flex flex-wrap gap-3">
                <a href="#manager" className="px-4 py-3 rounded-xl font-semibold transition-colors duration-200 hover:bg-white/20" style={{ background: 'rgba(255,255,255,.1)' }}>Настроить</a>
                <button onClick={rejectAll} className="px-4 py-3 rounded-xl font-semibold transition-colors duration-200 hover:bg-white/20" style={{ background: 'rgba(255,255,255,.1)' }}>Отклонить</button>
                <button onClick={acceptAll} className="px-4 py-3 rounded-xl font-semibold transition-opacity duration-200 hover:opacity-90" style={{ background: '#FFC300', color: '#102a43' }}>Принять всё</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}